import React from 'react';
import dayjs from 'dayjs';
import {
  AgxButton,
  AgxSpacer,
  AgxTypography,
  Breakpoints,
  Icon_SparkleForm,
  useWindowSize,
} from '@urbanx/agx.ui-components2';
import { useFeatureToggles } from 'hooks/useFeatureToggles';
import placeHolderImage from 'assets/images/placeholder-property.png';

import ContractCard from './ContractCard';
import BlankContractProgress from './BlankContractProgress';
import { useGetFileLink } from 'components/ui-components/File/fileApi';
import { openFileInNewTab } from 'utils/openFileInNewTab';
import { SolicitorContractDetails } from './SolicitorContractDetails';
import {
  CampaignCompletionState,
  CampaignDetailModel,
  CampaignDetailModelState,
  ContractDetailModel,
} from '@urbanx/agx.campaigns.client';
import { shouldShowBlankContractProgress } from './contractDetailsDisplay';
import { Feature } from '@urbanx/agx.agencies.client';

interface ContractDetailsProps {
  campaign: CampaignDetailModel;
  contractDetails: ContractDetailModel[];
  completionState: CampaignCompletionState;
  propertyImageUrls: { thumbnail: string | undefined };
}

const sortByDate = (a: ContractDetailModel, b: ContractDetailModel) =>
  dayjs(b.lastUpdateUtc).diff(dayjs(a.lastUpdateUtc));

const ContractDetails: React.FC<ContractDetailsProps> = ({
  campaign,
  contractDetails = [],
  completionState,
  propertyImageUrls: imageUrls,
}) => {
  const screenSize = useWindowSize();
  const getFileLink = useGetFileLink();
  const { isEnabled } = useFeatureToggles(campaign?.state);
  const automateLiveListings = isEnabled(Feature.AutomateLiveListings);
  const isNsw = campaign?.state === CampaignDetailModelState.NSW;
  const showBlankContractProgress = shouldShowBlankContractProgress(
    campaign?.state,
    automateLiveListings
  );

  const onDownloadBlankContract = async () => {
    if (campaign.blankSalesContract) {
      const fileLink = await getFileLink(campaign.blankSalesContract.containerFilePath);
      if (fileLink != null) {
        openFileInNewTab(screenSize === Breakpoints.Desktop, fileLink);
      }
    }
  };

  const noContracts = isNsw ? (
    <SolicitorContractDetails campaign={campaign} />
  ) : showBlankContractProgress ? (
    <BlankContractProgress blankContractProgress={campaign.blankContractProgress!} />
  ) : (
    <AgxSpacer fill contentAlignment="center" contentJustification="center" gap="spacing-md">
      <Icon_SparkleForm />
      <AgxTypography id="contract-details-empty-title" type="body" size="medium">
        You haven't created a contract yet!
      </AgxTypography>
      <AgxTypography id="contract-details-empty-description" type="body" size="medium">
        When you do, they'll appear here.
      </AgxTypography>
    </AgxSpacer>
  );

  const allContracts = contractDetails
    .map((contract: ContractDetailModel) => {
      return {
        ...contract,
        contractStage: contract.contractStage,
        formType: contract.inProgressForm?.formType,
      };
    })
    .sort(sortByDate)
    .map((contract, idx) => (
      <ContractCard
        key={idx}
        state={campaign.state ?? CampaignDetailModelState.QLD}
        contract={contract}
        isArchived={!!completionState?.completionType}
        propertyImage={imageUrls?.thumbnail ?? placeHolderImage}
      />
    ));

  const showAllContracts = allContracts.length > 0;

  return (
    <>
      <AgxSpacer gap="spacing-md" fill contentAlignment={'center'}>
        {showAllContracts ? allContracts : noContracts}
      </AgxSpacer>

      {campaign.blankContractIsLive && campaign.blankSalesContract && (
        <AgxButton
          id="agx-download-blank-contract"
          dataTestId="agx-download-blank-contract"
          variant="hollow"
          size={'large'}
          stretch
          text={`Download ${campaign?.blankSalesContractStatus ?? 'Blank'} Contract`}
          onClick={onDownloadBlankContract}
        />
      )}
    </>
  );
};

export default ContractDetails;
